import { ArrowUpRight } from 'lucide-react';
import { useT } from '@/i18n/provider';
import { LocalizedAnchor } from '@/components/LocalizedLink';

interface EngineerLink {
  key: string;
  href: string;
}

/**
 * The engine room. The homepage stays at decision-maker altitude; this strip
 * is where the technical audience branches off to the pages and docs that
 * used to be full homepage sections (MCP, pipelines, agent fleet, OTLP).
 */
const links: EngineerLink[] = [
  { key: 'lpql', href: '/docs/search/lpql-syntax' },
  { key: 'mcp', href: '/mcp' },
  { key: 'fleet', href: '/agents' },
  { key: 'otlp', href: '/docs/integrations/otlp' },
  { key: 'pipelines', href: '/docs/pipelines/building-pipelines' },
  { key: 'architecture', href: '/architecture' },
];

export function EngineerSection() {
  const t = useT();

  return (
    <section className="py-20 sm:py-28 bg-gray-950 dark:bg-gray-900/60 border-y border-gray-800" aria-labelledby="engineers-heading">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl animate-on-scroll">
          <p className="eyebrow text-primary-400">{t('engineers.eyebrow')}</p>
          <h2
            id="engineers-heading"
            className="mt-4 font-display text-3xl sm:text-4xl font-bold tracking-tight text-white"
          >
            {t('engineers.title')}
          </h2>
          <p className="mt-4 text-lg text-gray-400">
            {t('engineers.subtitle')}
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-gray-800 border border-gray-800 rounded-xl overflow-hidden">
          {links.map((link) => (
            <LocalizedAnchor
              key={link.key}
              href={link.href}
              className="animate-on-scroll group bg-gray-950 dark:bg-gray-900 p-6 hover:bg-gray-900 dark:hover:bg-gray-800/80 transition-colors"
            >
              <div className="flex items-center justify-between">
                <span className="text-base font-semibold text-white">
                  {t(`engineers.${link.key}.title`)}
                </span>
                <ArrowUpRight className="h-4 w-4 text-gray-600 group-hover:text-primary-400 transition-colors" aria-hidden="true" />
              </div>
              <p className="mt-2 text-sm text-gray-400 leading-relaxed">
                {t(`engineers.${link.key}.description`)}
              </p>
            </LocalizedAnchor>
          ))}
        </div>
      </div>
    </section>
  );
}
